import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Util } from "reactstrap";
import { ThemeProvider } from "styled-components";
import { getContextForReportName } from "../../utils/Helpers";
import { dark, light } from "../../utils/themeStyles";
import { getReport, GetAuth, PutAuth, PostAuth, DeleteAuth } from "../../utils/Verbs";
import { allRoutes } from "../../utils/routes";

const VsiAppContext = createContext();


export const VsiAppProvider = ({ children }) => {
  const [isVsiAppLoading, setIsVsiAppLoading] = useState(true);
  const [isVsiUserAuthenticated, setIsVsiUserAuthenticated] = useState(false);
  const [isSwitchingClient, setIsSwitchingClient] = useState(false);

  const [theme, setTheme] = useState(localStorage.getItem("vsiThemePreference") || "dark");
  const [sidebarOpen, setSidebarOpen] = useState(localStorage.getItem("vsiSideBarOpen") === "true");

  const toggleTheme = useCallback(themeToSet => {
    let newTheme = "";
    setTheme(theme => {
      newTheme = themeToSet ? themeToSet : theme === "dark" ? "light" : "dark";
      return newTheme;
    });
    localStorage.setItem("vsiThemePreference", newTheme);
  }, []);

  const {
    isAuthenticated: isAuth0Authenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently
  } = useAuth0();

  const [user, setUser] = useState({});
  const { userColConfig } = user;
  const [client, setClient] = useState({});

  const setPeriodId = useCallback(periodId => setPeriodsState(ps => ({ ...ps, periodId: periodId })), []);
  const setPeriodDate = useCallback(periodDate => setPeriodsState(ps => ({ ...ps, periodDate: periodDate })), []);

  // keeping these two broken out because they are more expensive to compute than the others
  const [periods, setPeriods] = useState([]);
  const [latestPeriods, setLatestPeriods] = useState([]);

  const [savedAutobotPieces, setSavedAutobotPieces] = useState({});
  const [allFavoritesItems, setAllFavoritesItems] = useState([]);

  const isLargeDesktopScreen = useMediaQuery({ maxWidth: 1441 });
  const isTabletScreen = useMediaQuery({ maxWidth: 768 });

  const history = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const pathNameSplit = history.pathname.split("/");
  const reportContext = useMemo(() => {
    let context = pathNameSplit[pathNameSplit.indexOf("reports") + 1];
      if (context === "storage") {
        if (pathNameSplit[pathNameSplit.indexOf("reports") + 2] === "device") {
          context = "device";
        }
      } else if (context === "virtual") {
        if (pathNameSplit[pathNameSplit.indexOf("reports") + 2] === "vm") {
          context = "vm";
        }
      } else if (context === "hosts") {
        if (pathNameSplit[pathNameSplit.indexOf("reports") + 2] === "host") {
          context = "host-detail";
        }
      } else if (context === "san-switch") {
        if (pathNameSplit[pathNameSplit.indexOf("reports") + 2] === "switch-topology") {
          context = "HOST-SWITCH-TOPOLOGY";
        }
      }
      return context
  }, [pathNameSplit]);

    const [periodsState, setPeriodsState] = useState({
      isGettingPeriods: true,
      isGettingLatestPeriods: true,
      periodId: null,
      periodDate: null,
      periodContext: reportContext
    });

  const {
    isGettingPeriods,
    isGettingLatestPeriods,
    periodId,
    periodDate,
    periodContext
  } = periodsState;

  const doPeriodsMatchReportContext = useMemo(() => {
    // for home screen, we don't have a reportContext, so we can't compare it to the periodContext
    if (!reportContext && !periodContext) return true;
    return !isGettingPeriods && !isGettingLatestPeriods && periodContext === reportContext;
  }, [periodContext, reportContext, isGettingPeriods, isGettingLatestPeriods]);

  const periodsRouteContextUriParam = useMemo(() => {
    let context = reportContext;
    let route = ``;
    // moved finops check to here
    if (context === "finops") {
      route += "&context=business-unit";
    } else if (context === "device") {
      route += `&context=storage&device_name=${searchParams.get(
        "device_name"
      )}`;
    } else if (context === "vm") {
      route += `&context=vm&device_name=${encodeURIComponent(
        searchParams.get("vcenter_name")
      )}&vm_name=${encodeURIComponent(
        searchParams.get("vm_name")
      )}&cluster_name=${encodeURIComponent(
        searchParams.get("cluster_name")
      )}&esx_host_name=${encodeURIComponent(
        searchParams.get("esx_host_name")
      )}`;
    } else if (context === "virtual") {
      route += `&context=virtual`;
      route += searchParams.get("vcenter_name")
        ? `&device_name=${encodeURIComponent(
          searchParams.get("vcenter_name")
        )}`
        : "";
      route += searchParams.get("cluster_name")
        ? `&cluster_name=${encodeURIComponent(
          searchParams.get("cluster_name")
        )}`
        : "";
      route += searchParams.get("esx_host_name")
        ? `&esx_host_name=${encodeURIComponent(
          searchParams.get("esx_host_name")
        )}`
        : "";
      route += searchParams.get("vm_name")
        ? `&vm_name=${encodeURIComponent(searchParams.get("vm_name"))}`
        : "";
    } else if (context === "host-detail") {
      route += `&context=host-detail&device_name=${searchParams.get(
        "device_name"
      )}&host_name=${searchParams.get("host_name")}`;
    } else if (context === "security") {
      route += `&context=security`;
    } else {
      route += `&context=${getContextForReportName(context)}`;
    }
    return route;
  }, [reportContext]);

  Util.setGlobalCssModule({
    "form-control": `form-control input-${theme} text-${
      theme === "dark" ? "white" : "black"
    }`,
    "form-label": `form-label text-${
      theme === "dark" ? "white" : "black"
    }`,
    "accordian-button": `accordian-button-test`
  });

  const getLatestPeriods = useCallback(async (client) => {
    setPeriodsState(p => ({
      ...p,
      isGettingLatestPeriods: true,
    }));
    const token = await getAccessTokenSilently();
    const periodData = await getReport(`/api/v6/report/latest_periods_for_client_proc?client_id=${client.clientId}`, null, token);
    setPeriodsState(p => ({
      ...p,
      isGettingLatestPeriods: false,
    }));
    setLatestPeriods(periodData?.data?.recordset);
  }, [getAccessTokenSilently, setLatestPeriods, setPeriodsState]);

  const updateUserConfigsWhenUserChanges = useCallback(userColConfig => {
    const newUser = { ...user };
    if (Array.isArray(userColConfig) && userColConfig.length > 0) {
      const columnsObj = userColConfig.reduce((acc, curr) => {
        if (!acc.hasOwnProperty(curr.path)) {
          acc[curr.path] = [];
        }
        acc[curr.path].push(curr);
        return acc;
      }, {});

      const newColByContext = allRoutes.reduce((acc, route) => {
        if (columnsObj.hasOwnProperty(route.path) && (!route.roles || route.roles.includes(newUser.role))) {
          if (!acc.hasOwnProperty(route.context)) {
            acc[route.context] = {};
          }
          acc[route.context][route.path] = {
            ...route,
            columns: columnsObj[route.path]
          };
        }
        return acc;
      }, {});

      newUser.keyedColumnsByContext = Object.keys(newColByContext);
      newUser.columnsByContext = newColByContext;
      newUser.userColumnsMap = columnsObj || {};
    }
    return newUser;
  }, [user]);

  useEffect(() => {
    if (userColConfig) {
      setUser(updateUserConfigsWhenUserChanges(userColConfig));
    }
  }, [userColConfig]);

  useEffect(() => {
    if (client.clientId) {
      getLatestPeriods(client);
    }
  }, [client.clientId]);

  useEffect(() => {
    if (isAuth0Authenticated && !isAuth0Loading) {
      setIsVsiAppLoading(true);

      const getUser = async () => {
        try {
          const token = await getAccessTokenSilently();
          const userData = await GetAuth("/api/v6/login/callback", token);
          const user = { ...userData?.data };
          console.log(userData?.data?.clientId);
          setUser({
            ...user,
            userColConfig: user?.userColConfig ?? [],
            id: user?._id
          });
          setAllFavoritesItems(user?.favorites);
          if (user.persistClient) {
            const activeClientData = await GetAuth(`/api/v6/admin/clients/mongo/${user.activeClient || user.client._id}`, token);
            console.log('persist', activeClientData?.data?.clientId)
            setClient({
              ...(activeClientData?.data)
            });
          } else {
            console.log('no persist', user?.client?.clientId)
            setClient({
              ...(user?.client ?? {})
            });
          }
          setIsVsiAppLoading(false);
          setIsVsiUserAuthenticated(true);
        } catch (err) {
          console.error("Failed to get token: ", err);
          // Later, we can force the user to login, if there is a problem fetching data from the API

          setIsVsiAppLoading(false);
          setIsVsiUserAuthenticated(false);
        }
      };
      getUser();
    } else if (!isAuth0Loading && !isAuth0Authenticated) {
      setIsVsiAppLoading(false);
    }
  }, [isAuth0Authenticated, isAuth0Loading, getAccessTokenSilently, setUser, setIsVsiUserAuthenticated, setIsVsiAppLoading]);

  useEffect(() => {
    if (client?.clientId) {
      const getAllPeriodsForClient = async () => {
        console.log(`|${reportContext}|`)
        if (reportContext) {
          setPeriodsState(ps => ({
            ...ps,
            isGettingPeriods: true,
            reportContext: reportContext,
          }));

          const token = await getAccessTokenSilently();
          const route = `/api/v6/report/periods_for_client_proc?client_id=${client.clientId}${periodsRouteContextUriParam}`;
          const data = await getReport(route, null, token);
          const periods = data?.data?.recordset;

          let splits = periods?.[0]?.collection_date?.split?.("-");
          let splitDay = splits?.[2]?.split?.("T");
          let day = splitDay?.[0];
          let chosenDate = `${splits?.[1]}/${day}/${splits?.[0]}`;

          setPeriodsState(ps => ({
            ...ps,
            isGettingPeriods: false,
            periodId: periods?.[0]?.period_id,
            periodDate: chosenDate,
            periodContext: reportContext,
          }));
          setPeriods(periods);
        } else {
          // no reports, no dates
          setPeriodsState(ps => ({
            ...ps,
            isGettingPeriods: false,
            periodId: null,
            periodDate: null,
            periodContext: reportContext,
          }));
          setPeriods([]);
        }
      };

      getAllPeriodsForClient();
    }
  }, [client.clientId, reportContext, getAccessTokenSilently, periodsRouteContextUriParam]);

  const switchClient = useCallback(async (clientId) => {
    if (["super_user", "super_user_read_only"].includes(user.role) && (clientId !== client.clientId)) {
      setIsSwitchingClient(true);
      try {
        const token = await getAccessTokenSilently();
        const clientData = await GetAuth(`/api/v6/admin/clients/${clientId}`, token);
        setClient(clientData?.data);

        if (user.persistClient) {
          try {
            await PutAuth("/api/v6/admin/users/" + user._id, {
              email: user.email,
              activeClient: clientData.data._id
            }, token);
          } catch (err) {
            console.error(err);
          }
        }
      } catch (err) {
        console.error("Failed to get client: ", err);
      } finally {
        setIsSwitchingClient(false);
      }

      if (history.pathname !== "/home") {
        navigate("/home");
      }
    }
  }, [user.role, client.clientId, navigate]);

  const getPeriodId = useCallback((context) => {
    if (latestPeriods.length) {

      const detailSupported = ["Host", "Files", "Device"];
      let periodId;
      const contextLower = context.toLowerCase();
      // Accounting for the different contexts for Files screen
      if (contextLower === "files") {
        periodId = latestPeriods?.filter(a => a.context.toUpperCase() === "FILE-ANALYSIS")[0].period_id;
      } else if (contextLower === "device") {
        periodId = latestPeriods?.filter(a => a.context.toUpperCase() === "STORAGE")[0].period_id;
      } else if (contextLower === "tagging" || contextLower === "finops") {
        periodId = latestPeriods?.filter(a => a.context.toUpperCase() === "BUSINESS UNIT")[0].period_id;
      } else if (contextLower === "host") {
        periodId = latestPeriods?.filter(a => a.context.toUpperCase() === "HOSTS")[0].period_id;
      } else if (detailSupported.indexOf(context.toUpperCase()) < 0) {
        periodId = latestPeriods?.filter(a => a.context.toUpperCase() === context.toUpperCase())[0].period_id;
      }

      if (searchParams.get("period_id")) {
        periodId = searchParams.get("period_id");
      }
      return periodId;
    }
  }, [latestPeriods, searchParams]);

  const updateReportMetadata = useCallback(async (metadata, params = {}, doDelete = false) => {
    /**
     * Performs all CRUD operations for reportMetadata
     *
     * This function takes 2 objects and boolean to determine which operation to perform
     *
     * @param {object} metadata - Holds the unique feature-based flags that will be useful in accomplishing your new feature.
     * Ex: { activeRangeIndex: 1, notes: 'this is a test', isCheckboxSelected: true }
     * @param {object} params - Holds the unique identifiers other than the path such as device name, vcenter name, etc. which is exactly how the grid configs work.
     * Ex: { deviceName: 'TMCFLASH', vmName: 'new-vm' }
     * @param {boolean} doDelete - a boolean determining if you just want to delete the record. Easy enough
     */

    let existingMetadata = user?.reportMetadata?.filter(config => config.path === window.location.pathname && client._id === config.client);
    const keyedParams = Object.keys(params);
    if (keyedParams.length > 0) {
      existingMetadata = existingMetadata?.filter(config => {
        return keyedParams.every(key => config?.params?.[key] === params?.[key]);
      });
    }
    if (existingMetadata.length > 0) {
      const [foundMetadata] = existingMetadata;
      const token = await getAccessTokenSilently();
      if (foundMetadata._id) {
        if (doDelete) {
          // DO DELETE
          try {
            const deleteMd = await DeleteAuth(`/api/v6/admin/report_metadata/${user._id}/${foundMetadata._id}`, token);
            if (deleteMd && deleteMd.status === 201) {
              setUser(user => {
                return {
                  ...user,
                  reportMetadata: user?.reportMetadata.filter(md => foundMetadata._id !== md._id)
                };
              });
            }
          } catch (e) {
            console.error(e);
          }
          return;
        }
        // DO PUT
        try {
          const putMd = await PutAuth(`/api/v6/admin/report_metadata/${user._id}/${foundMetadata._id}`, {
            path: window.location.pathname,
            metadata,
            params: params || {},
            client: client._id
          }, token);

          if (putMd && putMd.status === 200) {
            setUser(user => {
              return {
                ...user,
                reportMetadata: user?.reportMetadata.map(md => {
                  if (md._id === foundMetadata._id || (md.path === window.location.pathname && params ? Object.keys(params).every(key => md?.params?.[key] === params?.[key]) : true)) {
                    return {
                      ...md,
                      client: client._id,
                      metadata: {
                        ...(md.metadata || {}),
                        ...metadata
                      },
                      params: {
                        ...(md.params || {}),
                        ...params
                      }
                    };
                  }
                  return md;
                })
              };
            });
          }
        } catch (e) {
          console.error(e);
        }
      }
    } else {
      // DO POST
      try {
        const token = await getAccessTokenSilently();
        const postMd = await PostAuth(`/api/v6/admin/report_metadata`, {
          path: window.location.pathname,
          metadata,
          params: params || {},
          client: client._id,
          user: user._id
        }, token);
        if (postMd && postMd.status === 201) {
          setUser(user => {
            return {
              ...user,
              reportMetadata: [
                ...user.reportMetadata,
                {
                  // I could probably spread the whole thing out here, but I just want to handle what I can
                  _id: postMd.data.data._id,
                  client: client._id,
                  metadata: {
                    ...metadata
                  },
                  path: window.location.pathname,
                  params: {
                    ...params
                  }
                }]
            };
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [user.reportMetadata, client._id, getAccessTokenSilently]);

  const contextValue = useMemo(() => ({
    theme,
    toggleTheme,
    sidebarOpen,
    setSidebarOpen,
    isLargeDesktopScreen,
    isTabletScreen,
    isVsiAppLoading,
    isGettingPeriods,
    isGettingLatestPeriods,
    isSwitchingClient,
    isVsiUserAuthenticated,
    client,
    user,
    setUser,
    getAccessTokenSilently,
    periods,
    latestPeriods,
    periodId,
    periodDate,
    getPeriodId,
    setPeriodId,
    setPeriodDate,
    switchClient,
    savedAutobotPieces,
    setSavedAutobotPieces,
    allFavoritesItems,
    setAllFavoritesItems,
    updateReportMetadata,
    reportContext,
    doPeriodsMatchReportContext,
  }), [
    theme, toggleTheme, sidebarOpen, setSidebarOpen,
    isLargeDesktopScreen, isTabletScreen, isVsiAppLoading,
    isGettingPeriods, isGettingLatestPeriods, isSwitchingClient, reportContext,
    isVsiUserAuthenticated, client, user, setUser, getAccessTokenSilently,
    periods, latestPeriods, periodId, periodDate, getPeriodId,
    setPeriodId, setPeriodDate, switchClient, savedAutobotPieces,
    setSavedAutobotPieces, allFavoritesItems, setAllFavoritesItems,
    updateReportMetadata, doPeriodsMatchReportContext
  ]);

  return (
        <VsiAppContext.Provider value={contextValue}>
          <ThemeProvider theme={theme === "dark" ? dark : light}>
            {children}
          </ThemeProvider>
        </VsiAppContext.Provider>
  );
};

export const useVsiAppContext = () => useContext(VsiAppContext);


